import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { Navbar, Nav, Container, Button, Form, Overlay, Popover, Modal } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createLogger } from '../utils/debugLogger';
import { getTickers, getLastUpdateTime, saveSelectedMarketIndexes, getSelectedMarketIndexes, validateTicker, getSelectedStatusTickers, getTopNavigationLinks, updateTopNavigationLinks, getPipelineCounts } from '../services/api';
import { subscribeToTickerUpdates, subscribeToMarketIndexUpdates, subscribeToSocketStatus } from '../services/socketService';
import { useTickerUpdates } from '../hooks/useTickerUpdates';
import LogoutConfirmation from './LogoutConfirmation';
import StatusButton from './StatusButton';
import HeartbeatIndicator from './HeartbeatIndicator';
import DetachableClock from './DetachableClock';
import DetachableCalculator from './DetachableCalculator';
import StrategyPresetModal from './StrategyPresetModal';
import AppVersion from './AppVersion';
import { ThemeContext } from '../contexts/ThemeContext';
import { useMarketCondition } from '../contexts/MarketConditionContext';
import { MARKET_CONDITION_OPTIONS, getMarketConditionOption } from '../utils/marketConditions';
import {
  STRATEGY_PRESET_MODAL_EVENT,
  STRATEGY_PRESET_MODAL_STORAGE_KEY,
  getStrategyPresetModalVisibility,
  hideStrategyPresetModal
} from '../utils/strategyPresetModalManager';

// Create debug logger for this module
const logger = createLogger('navigation', 'Top nav + socket status', false, 'DEBUG');
const allowSelfRegistration = process.env.REACT_APP_ALLOW_SELF_REGISTRATION === 'true';

// Default nav row layouts (static, defined once at module scope)
const NAV_ROW_1_DEFAULT = ['tickers', 'transactions', 'trades', 'orders', 'plan', 'livermore', 'play', 'wisdom', 'strategies'];
const NAV_ROW_2_DEFAULT = ['screen', 'watchlist', 'ants', 'newipos', 'etfs', 'stopouts', 'steroids', 'leaders', 'vcp', 'basing', 'eps', 'athigh', 'turnarounds', 'cyclicals', 'gleaders', 'themes', 'readylist', 'inverses', 'hvol', 'minervini'];
const NAV_ROW_3_DEFAULT = [];

const getSavedNavRows = () => {
  if (typeof window === 'undefined') {
    return { row1: NAV_ROW_1_DEFAULT, row2: NAV_ROW_2_DEFAULT, row3: NAV_ROW_3_DEFAULT };
  }

  try {
    const allowedKeys = new Set([...NAV_ROW_1_DEFAULT, ...NAV_ROW_2_DEFAULT, ...NAV_ROW_3_DEFAULT]);
    const savedRow1 = JSON.parse(localStorage.getItem('topNavRow1Order') || '[]');
    const savedRow2 = JSON.parse(localStorage.getItem('topNavRow2Order') || '[]');
    const savedRow3 = JSON.parse(localStorage.getItem('topNavRow3Order') || '[]');

    const row1 = Array.isArray(savedRow1)
      ? savedRow1.filter(key => allowedKeys.has(key))
      : [];
    const usedKeys = new Set(row1);

    const row2 = Array.isArray(savedRow2)
      ? savedRow2.filter(key => allowedKeys.has(key) && !usedKeys.has(key))
      : [];
    row2.forEach(key => usedKeys.add(key));

    const row3 = Array.isArray(savedRow3)
      ? savedRow3.filter(key => allowedKeys.has(key) && !usedKeys.has(key))
      : [];
    row3.forEach(key => usedKeys.add(key));

    NAV_ROW_1_DEFAULT.forEach(key => {
      if (!usedKeys.has(key)) {
        row1.push(key);
        usedKeys.add(key);
      }
    });

    NAV_ROW_2_DEFAULT.forEach(key => {
      if (!usedKeys.has(key)) {
        row2.push(key);
        usedKeys.add(key);
      }
    });

    allowedKeys.forEach(key => {
      if (!usedKeys.has(key)) {
        row3.push(key);
      }
    });

    return { row1, row2, row3 };
  } catch (error) {
    logger.warn('Navigation: Failed to read saved nav order, using defaults', error);
    return { row1: NAV_ROW_1_DEFAULT, row2: NAV_ROW_2_DEFAULT, row3: NAV_ROW_3_DEFAULT };
  }
};

const mergeNavRows = (row1, row2, row3 = []) => {
  const allowedKeys = new Set([...NAV_ROW_1_DEFAULT, ...NAV_ROW_2_DEFAULT, ...NAV_ROW_3_DEFAULT]);
  const usedKeys = new Set();
  const mergedRow1 = Array.isArray(row1) ? row1.filter(key => allowedKeys.has(key)) : [];
  mergedRow1.forEach(key => usedKeys.add(key));
  const mergedRow2 = Array.isArray(row2) ? row2.filter(key => allowedKeys.has(key) && !usedKeys.has(key)) : [];
  mergedRow2.forEach(key => usedKeys.add(key));
  const mergedRow3 = Array.isArray(row3) ? row3.filter(key => allowedKeys.has(key) && !usedKeys.has(key)) : [];
  mergedRow3.forEach(key => usedKeys.add(key));

  NAV_ROW_1_DEFAULT.forEach(key => {
    if (!usedKeys.has(key)) {
      mergedRow1.push(key);
      usedKeys.add(key);
    }
  });

  NAV_ROW_2_DEFAULT.forEach(key => {
    if (!usedKeys.has(key)) {
      mergedRow2.push(key);
      usedKeys.add(key);
    }
  });

  allowedKeys.forEach(key => {
    if (!usedKeys.has(key)) {
      mergedRow3.push(key);
    }
  });

  return { row1: mergedRow1, row2: mergedRow2, row3: mergedRow3 };
};

function Navigation({ isAuthenticated, onLogout, toggleMobileNav, isMobileNavVisible }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { marketCondition, updateMarketCondition } = useMarketCondition();
  const marketConditionOption = getMarketConditionOption(marketCondition);
  const marketConditionDarkTextColor = marketConditionOption.darkText;
  const {
    effectiveTheme,
    weatherThemeEnabled,
    selectedWeatherTheme,
  } = useContext(ThemeContext);
  const isDark = effectiveTheme === 'dark';
  const navbarBg = isDark ? 'dark' : 'light';
  const navbarVariant = navbarBg;
  const [searchTicker, setSearchTicker] = useState('');
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [lastPriceUpdateTime, setLastPriceUpdateTime] = useState(null);
  const [lastPriceUpdateCount, setLastPriceUpdateCount] = useState(0);
  const [updateIntervalMinutes, setUpdateIntervalMinutes] = useState(1); // Default to 1 minute
  // Kept in sync with updateIntervalMinutes so the mount-once WebSocket subscription
  // below can read the latest value without needing to resubscribe on every change.
  const updateIntervalMinutesRef = useRef(updateIntervalMinutes);
  useEffect(() => {
    updateIntervalMinutesRef.current = updateIntervalMinutes;
  }, [updateIntervalMinutes]);
  const [socketStatus, setSocketStatus] = useState('disconnected');
  const hasTimestampRef = useRef(false);
  const [storedInviteToken, setStoredInviteToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('pendingInviteToken');
  });
  const inviteTokenFromUrl = useMemo(() => {
    return new URLSearchParams(location.search).get('invite');
  }, [location.search]);
  const shouldShowRegisterLink = allowSelfRegistration || Boolean(inviteTokenFromUrl || storedInviteToken);

  useEffect(() => {
    const handleInviteUpdate = () => {
      if (typeof window === 'undefined') return;
      setStoredInviteToken(localStorage.getItem('pendingInviteToken'));
    };

    window.addEventListener('storage', handleInviteUpdate);
    window.addEventListener('pending-invite-updated', handleInviteUpdate);

    return () => {
      window.removeEventListener('storage', handleInviteUpdate);
      window.removeEventListener('pending-invite-updated', handleInviteUpdate);
    };
  }, []);
  
  // Distributed system status
  const [distributedStatus, setDistributedStatus] = useState('idle');
  const [lockStatus, setLockStatus] = useState(null);
  const [rateLimitStatus, setRateLimitStatus] = useState(null);
  
  // Dual-channel ticker updates
  // Debug socket status changes
  useEffect(() => {
    logger.debug('Socket status changed', { status: socketStatus });
  }, [socketStatus]);
  const [marketInfo, setMarketInfo] = useState(null);
  const [marketIndexes, setMarketIndexes] = useState([]);
  const [, setTimeRefresh] = useState(0);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileRef = useRef(null);
  const [username, setUsername] = useState('User');
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [showIndexSelector, setShowIndexSelector] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [customTickerInput, setCustomTickerInput] = useState('');
  const [customTickerError, setCustomTickerError] = useState('');
  const indexSelectorRef = useRef(null);
  const [isStrategyModalVisible, setIsStrategyModalVisible] = useState(() =>
    getStrategyPresetModalVisibility()
  );
  const cacheNavRows = useCallback((row1, row2, row3 = []) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('topNavRow1Order', JSON.stringify(row1));
      localStorage.setItem('topNavRow2Order', JSON.stringify(row2));
      localStorage.setItem('topNavRow3Order', JSON.stringify(row3));
    } catch (error) {
      logger.warn('Navigation: Failed to cache nav order', error);
    }
  }, []);
  const persistNavOrder = useCallback(async (row1, row2, row3 = []) => {
    cacheNavRows(row1, row2, row3);
    try {
      await updateTopNavigationLinks({ row1, row2, row3 });
    } catch (error) {
      logger.warn('Navigation: Failed to persist nav order', error);
    }
  }, [cacheNavRows]);

  
  // Current time for real-time clock display
  const [, setCurrentTime] = useState(new Date());

  const initialNavRows = useMemo(() => getSavedNavRows(), []);
  const [navRow1, setNavRow1] = useState(() => initialNavRows.row1);
  const [navRow2, setNavRow2] = useState(() => initialNavRows.row2);
  const [navRow3, setNavRow3] = useState(() => initialNavRows.row3);

  // Collapsible navigation state
  const [isNavCollapsed, setIsNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const stored = window.localStorage.getItem('navCollapsed');
      return stored === 'true';
    } catch (err) {
      return false;
    }
  });

  // Save collapsed state to localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('navCollapsed', isNavCollapsed.toString());
    } catch (err) {
      logger.warn('Navigation: Failed to save collapsed state', err);
    }
  }, [isNavCollapsed]);

  const [stopoutsCount, setStopoutsCount] = useState(() => {
    if (typeof window === 'undefined') return 0;
    try {
      const stored = window.localStorage.getItem('stopouts_count');
      return stored ? parseInt(stored, 10) || 0 : 0;
    } catch (err) {
      return 0;
    }
  });

  const readCount = (key) => {
    if (typeof window === 'undefined') return 0;
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? parseInt(stored, 10) || 0 : 0;
    } catch (err) {
      return 0;
    }
  };
  const [basingCount, setBasingCount] = useState(() => readCount('basing_count'));
  const [vcpCount, setVcpCount] = useState(() => readCount('vcp_count'));
  const [watchlistCount, setWatchlistCount] = useState(() => readCount('watchlist_count'));
  const [readylistCount, setReadylistCount] = useState(() => readCount('readylist_count'));
  const [tickersCount, setTickersCount] = useState(() => readCount('tickers_count'));

  const [navOrderReady, setNavOrderReady] = useState(false);
  const navOrderPersistSkipRef = useRef(true);

  const [draggingNavKey, setDraggingNavKey] = useState(null);
  const [, setDraggingNavRow] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const handleVisibilityChange = (event) => {
      if (event?.detail?.visible === undefined) {
        return;
      }
      setIsStrategyModalVisible(event.detail.visible);
    };

    const handleStorageChange = (event) => {
      if (event.key === STRATEGY_PRESET_MODAL_STORAGE_KEY) {
        setIsStrategyModalVisible(event.newValue === 'true');
      }
    };

    window.addEventListener(STRATEGY_PRESET_MODAL_EVENT, handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(STRATEGY_PRESET_MODAL_EVENT, handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchNavOrder = async () => {
      let appliedBackendOrder = false;
      try {
        const response = await getTopNavigationLinks();
        const { row1, row2, row3 } = response?.data || {};
        if (
          isMounted &&
          Array.isArray(row1) &&
          Array.isArray(row2)
        ) {
          const merged = mergeNavRows(row1, row2, row3);
          setNavRow1(merged.row1);
          setNavRow2(merged.row2);
          setNavRow3(merged.row3);
          cacheNavRows(merged.row1, merged.row2, merged.row3);
          appliedBackendOrder = true;
        }
      } catch (error) {
        logger.error('Navigation: Failed to fetch top navigation links', error);
      } finally {
        if (isMounted) {
          navOrderPersistSkipRef.current = appliedBackendOrder;
          setNavOrderReady(true);
        }
      }
    };

    fetchNavOrder();

    return () => {
      isMounted = false;
    };
  }, [cacheNavRows]);

  const handleStrategyModalClose = () => {
    setIsStrategyModalVisible(false);
    hideStrategyPresetModal();
  };

  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  // Initialize detached states from localStorage
  const [isClockDetached, setIsClockDetached] = useState(() => {
    const saved = localStorage.getItem('isClockDetached');
    return saved === 'true';
  });
  const [isCalculatorDetached, setIsCalculatorDetached] = useState(() => {
    const saved = localStorage.getItem('isCalculatorDetached');
    return saved === 'true';
  });
  const clockRef = useRef(null);
  const [showMarketConditionModal, setShowMarketConditionModal] = useState(false);

  useEffect(() => {
    hasTimestampRef.current = Boolean(lastUpdateTime);
  }, [lastUpdateTime]);

  // Persist detached states to localStorage
  useEffect(() => {
    localStorage.setItem('isClockDetached', isClockDetached.toString());
  }, [isClockDetached]);

  useEffect(() => {
    localStorage.setItem('isCalculatorDetached', isCalculatorDetached.toString());
  }, [isCalculatorDetached]);

  const fetchSelectedStatusTickers = useCallback(async () => {
    if (!isAuthenticated) {
      setMarketIndexes([]);
      return;
    }

    try {
      logger.debug('Fetching selected status tickers...');
      const response = await getSelectedStatusTickers();
      logger.debug('Selected status tickers API response', response.data);
      logger.debug('Setting market indexes state', response.data);
      setMarketIndexes(response.data || []);
    } catch (error) {
      logger.error('Error fetching selected status tickers:', error);
      setMarketIndexes([]);
    }
  }, [isAuthenticated]);

  const refreshLastUpdateTime = useCallback(async () => {
    if (!isAuthenticated) {
      const fallback = new Date();
      setLastUpdateTime(fallback);
      hasTimestampRef.current = true;
      return;
    }

    let updated = false;

    try {
      // Use the dedicated function to get the last update time
      const response = await getLastUpdateTime();

      // Update market info if it's included in the response
      if (response.data && response.data.market_closed !== undefined) {
        logger.debug('Setting market info from last update time response', {
          market_closed: response.data.market_closed,
          market_info: response.data.market_info
        });
        setMarketInfo({
          market_closed: response.data.market_closed,
          market_info: response.data.market_info || {}
        });
      }

      if (response.data && response.data.last_update_time) {
        const lastUpdateTimestamp = response.data.last_update_time;

        try {
          const parsedDate = new Date(lastUpdateTimestamp);
          if (!isNaN(parsedDate.getTime()) && parsedDate <= new Date()) {
            logger.debug('Navigation setting lastUpdateTime from server', parsedDate);
            setLastUpdateTime(parsedDate);
            hasTimestampRef.current = true;
            updated = true;
          } else {
            logger.warn('Navigation: Invalid timestamp from server, using recent valid time');
            const tickersResponse = await getTickers();
            const tickers = tickersResponse.data || [];
            let mostRecentDate = null;

            for (const ticker of tickers) {
              if (ticker.last_updated) {
                try {
                  const tickerDate = new Date(ticker.last_updated);
                  if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                    if (!mostRecentDate || tickerDate > mostRecentDate) {
                      mostRecentDate = tickerDate;
                    }
                  }
                } catch (e) {
                  // Skip invalid timestamps
                }
              }
            }

            if (mostRecentDate) {
              logger.debug('Navigation: Using most recent ticker timestamp', mostRecentDate);
              setLastUpdateTime(mostRecentDate);
              hasTimestampRef.current = true;
              updated = true;
            }
          }
        } catch (e) {
          logger.error('Navigation: Error parsing last update timestamp:', e);
        }
      } else {
        logger.warn('Navigation: No last_update_time in response');
      }
    } catch (err) {
      logger.error('Navigation: Error fetching last update time:', err);
    }

    if (!updated && !hasTimestampRef.current) {
      logger.warn('Navigation: All timestamp fetching attempts failed, using current time as last resort');
      const fallback = new Date();
      setLastUpdateTime(fallback);
      hasTimestampRef.current = true;
    }
  }, [isAuthenticated]);

  const {
    pollingStatus,
    setPollingInterval,
    isWebSocketConnected,
    isPollingActive,
    lastUpdateSource,
    totalUpdates,
    webSocketUpdates,
    pollingUpdates,
  } = useTickerUpdates({
    enablePolling: true,
    onUpdate: (data) => {
      if (data?.pollingCycleComplete) {
        logger.debug('Navigation: Polling cycle completed, refreshing navigation data', {
          tickerCount: data?.updated_tickers?.length || 0
        });
        fetchSelectedStatusTickers();
        refreshLastUpdateTime();
      }
    }
  });

  const preSearchLocation = useRef(null);

  const handleSearchNavigate = (value) => {
    const q = value.trim().toUpperCase();
    if (!q) return;
    if (!location.pathname.startsWith('/search')) {
      preSearchLocation.current = location.pathname + location.search;
    }
    navigate(`/search?ticker=${q}`);
  };

  const handleSearchClear = () => {
    setSearchTicker('');
    if (location.pathname.startsWith('/search')) {
      if (preSearchLocation.current) {
        navigate(preSearchLocation.current);
        preSearchLocation.current = null;
      } else {
        navigate(-1);
      }
    }
  };

  // Subscribe to centralized socket status (App.js handles socket initialization)
  useEffect(() => {
    const unsubscribe = subscribeToSocketStatus((status) => {
      setSocketStatus(status);
    });
    return () => unsubscribe();
  }, []);

  // Listen for ticker updates via WebSocket
  useEffect(() => {
    const unsubscribe = subscribeToTickerUpdates((data) => {
      logger.debug('=== NAVIGATION WEBSOCKET DEBUG ===');
      logger.debug('Navigation received ticker update via WebSocket', {
        hasUpdatedTickers: Boolean(data.updated_tickers && data.updated_tickers.length > 0),
        tickerCount: data?.updated_tickers?.length || 0,
        latestUpdateTime: data.latest_update_time,
        timeProperty: data.time,
        isTimestampEvent: Boolean(
          (data.message && data.message.includes('timestamp')) ||
          (data.message && data.message.includes('showing last price'))
        ),
        sampleTickerData: data.updated_tickers?.[0] || 'No tickers'
      });
      
      // Check if this is a real price update (has actual ticker data)
      const hasRealTickerUpdates = data && data.updated_tickers && data.updated_tickers.length > 0;
      
      if (hasRealTickerUpdates) {
        logger.debug(`Navigation: 🎯 REAL PRICE UPDATE detected with ${data.updated_tickers.length} tickers`);
        setLastPriceUpdateTime(new Date());
        setLastPriceUpdateCount(data.updated_tickers.length);
        
        // Extract status bar tickers from the ticker update and update market indexes display
        const statusBarSymbols = backendSelectedIndexesRef.current; // Current status bar symbols
        const statusBarTickers = data.updated_tickers.filter(ticker => 
          statusBarSymbols.includes(ticker.symbol)
        );
        
        if (statusBarTickers.length > 0) {
          logger.debug(`Navigation: Updating ${statusBarTickers.length} status bar tickers from ticker_update event`);
          
          // Update market indexes state with the status bar ticker data
          setMarketIndexes(prevIndexes => {
            const updatedIndexes = [...prevIndexes];
            
            statusBarTickers.forEach(updatedTicker => {
              const index = updatedIndexes.findIndex(idx => idx.symbol === updatedTicker.symbol);
              if (index !== -1) {
                // Update existing ticker
                updatedIndexes[index] = {
                  ...updatedIndexes[index],
                  current_price: updatedTicker.current_price,
                  previous_close: updatedTicker.previous_close,
                  percent_change: updatedTicker.percent_change,
                  last_updated: updatedTicker.last_updated
                };
              } else {
                // Add new ticker if not found
                updatedIndexes.push({
                  symbol: updatedTicker.symbol,
                  current_price: updatedTicker.current_price,
                  previous_close: updatedTicker.previous_close,
                  percent_change: updatedTicker.percent_change,
                  last_updated: updatedTicker.last_updated
                });
              }
            });
            
            return updatedIndexes;
          });
        }
      }
      
      // Always update the timestamp regardless of whether this is a timestamp event or ticker update
      // Create a function to validate and set timestamp with debug
      const trySetTimestamp = (source, timestamp) => {
        try {
          if (!timestamp) {
            logger.debug(`Navigation: ${source} - no timestamp provided`);
            return false;
          }
          
          const now = new Date();
          const parsedDate = new Date(timestamp);
          
          logger.debug(`Navigation: ${source} timestamp validation`, {
            timestamp: timestamp,
            parsedDate: parsedDate,
            isValidDate: !isNaN(parsedDate),
            currentTime: now,
            timeDifference: parsedDate.getTime() - now.getTime(),
            isInFuture: parsedDate > now
          });
          
          // Only use timestamp if it's valid and not more than 1 minute in the future (to handle small clock differences)
          if (!isNaN(parsedDate) && parsedDate <= new Date(now.getTime() + 60000)) { // Allow up to 1 minute in future
            logger.debug(`Navigation: ✅ Setting lastUpdateTime from ${source}:`, parsedDate);
            setLastUpdateTime(parsedDate);
            return true;
          } else {
            logger.debug(`Navigation: ❌ Rejecting ${source} timestamp - invalid or too far in future`);
          }
          return false;
        } catch (e) {
          logger.error(`Navigation: Error parsing ${source} timestamp:`, e);
          return false;
        }
      };

      // Update market status if included in the data
      if (data.market_closed !== undefined && data.market_info) {
        logger.debug('Navigation: Setting market info', {
          market_closed: data.market_closed,
          market_info: data.market_info
        });
        setMarketInfo({
          market_closed: data.market_closed,
          market_info: data.market_info
        });
      }
      
      // Update interval from ticker update events
      if (data.thread_interval_minutes && data.thread_interval_minutes !== updateIntervalMinutesRef.current) {
        logger.debug('Navigation: Setting update interval', {
          intervalMinutes: data.thread_interval_minutes
        });
        setUpdateIntervalMinutes(data.thread_interval_minutes);
      }

      // Update distributed system status
      if (data.distributed_status) {
        logger.debug('Navigation: Setting distributed status', data.distributed_status);
        setDistributedStatus(data.distributed_status);
      }

      if (data.lock_status) {
        logger.debug('Navigation: Setting lock status', data.lock_status);
        setLockStatus(data.lock_status);
      }

      if (data.rate_limit_status) {
        logger.debug('Navigation: Setting rate limit status', data.rate_limit_status);
        setRateLimitStatus(data.rate_limit_status);
      }
      
      // Try to find and set a valid timestamp in this priority order:
      // 1. Most recent ticker timestamp
      // 2. latest_update_time from the event
      // 3. time property from the event
      // 4. Fall back to current time
      
      // First try to find the most recent ticker timestamp if we have tickers
      let foundValidTimestamp = false;
      if (data && data.updated_tickers && data.updated_tickers.length > 0) {
        let mostRecentDate = null;
        let mostRecentTimeString = null;
        
        // Loop through all tickers to find the most recent valid timestamp
        for (const ticker of data.updated_tickers) {
          if (ticker.last_updated) {
            try {
              const tickerDate = new Date(ticker.last_updated);
              // Make sure it's a valid date in the past
              if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                if (!mostRecentDate || tickerDate > mostRecentDate) {
                  mostRecentTimeString = ticker.last_updated;
                  mostRecentDate = tickerDate;
                }
              }
            } catch (e) { 
              // Skip invalid timestamp
            }
          }
        }
        
        // Use the most recent ticker timestamp if found
        if (mostRecentDate) {
          logger.debug('Navigation: Using most recent ticker timestamp', mostRecentTimeString);
          setLastUpdateTime(mostRecentDate);
          foundValidTimestamp = true;
        }
      }
      
      // If no valid ticker timestamp was found, try latest_update_time
      if (!foundValidTimestamp && data.latest_update_time) {
        foundValidTimestamp = trySetTimestamp('latest_update_time', data.latest_update_time);
      }
      
      // If still no valid timestamp, try data.time
      if (!foundValidTimestamp && data.time) {
        foundValidTimestamp = trySetTimestamp('data.time', data.time);
      }
      
      // Last resort - use current time
      if (!foundValidTimestamp) {
        logger.debug('Navigation: No valid timestamp found, using current time');
        setLastUpdateTime(new Date());
      }
    });

    // Subscribe to market index updates
    const unsubscribeMarket = subscribeToMarketIndexUpdates((data) => {
      logger.debug('=== MARKET INDEX UPDATE DEBUG ===');
      logger.debug('Received market index update', data);
      
      const indexes = (data && (data.indexes || data.market_data)) || null;
      if (indexes) {
        // Only update market indexes if the data contains actual ticker information
        // Check if the first item has ticker properties like 'symbol', 'current_price', etc.
        const isTickerData = Array.isArray(indexes) && indexes.length > 0 && 
                            indexes.some(item => item && (item.symbol || item.current_price !== undefined));
        
        if (isTickerData) {
          logger.debug('Market index update contains ticker data, updating indexes');
          setMarketIndexes(indexes);
        } else {
          logger.debug('Market index update contains status info only, preserving existing ticker data');
        }
        
        // Always update the last update time when receiving market updates
        if (data.time) {
          try {
            const updateTime = new Date(data.time);
            if (!isNaN(updateTime) && updateTime <= new Date()) {
              logger.debug('Setting last update time from market index update', updateTime);
              setLastUpdateTime(updateTime);
            }
          } catch (e) {
            logger.error('Error parsing timestamp from market index update:', e);
          }
        }
      }
      logger.debug('=== END MARKET INDEX UPDATE DEBUG ===');
    });
    
    return () => {
      unsubscribe();
      unsubscribeMarket();
    };
  }, []);

  // Update relative time text every minute
  useEffect(() => {
    const intervalId = setInterval(() => {
      // Force re-render to update relative time displays
      setTimeRefresh(prev => prev + 1);
    }, 60000); // Update every minute
    
    return () => clearInterval(intervalId);
  }, []);

  // Listen for global event to open market condition modal
  useEffect(() => {
    const openHandler = () => setShowMarketConditionModal(true);
    window.addEventListener('openMarketConditionModal', openHandler);
    return () => window.removeEventListener('openMarketConditionModal', openHandler);
  }, []);

  // Listen for tickerUpdate events from other components
  useEffect(() => {
    const handleTickerUpdate = (event) => {
      logger.debug('Navigation received tickerUpdate event', {
        time: event.detail.time,
        source: event.detail.source
      });

      try {
        // Validate the timestamp from the event
        const updateTime = new Date(event.detail.time);
        if (!isNaN(updateTime) && updateTime <= new Date()) {
          logger.debug('Setting last update time from tickerUpdate event', updateTime);
          setLastUpdateTime(updateTime);
        } else {
          logger.warn('Invalid timestamp in tickerUpdate event', event.detail.time);
        }
      } catch (e) {
        logger.error('Error processing tickerUpdate event:', e);
      }
    };
    
    // Add event listener
    window.addEventListener('tickerUpdate', handleTickerUpdate);
    
    // Clean up
    return () => {
      window.removeEventListener('tickerUpdate', handleTickerUpdate);
    };
  }, []);

  // Fetch market indexes on component mount
  useEffect(() => {
    fetchSelectedStatusTickers();
  }, [fetchSelectedStatusTickers]);

  // Listen for ticker selection changes from chart
  useEffect(() => {
    const handleTickerSelectionChanged = () => {
      // Refresh status tickers when selection changes
      fetchSelectedStatusTickers();
    };

    window.addEventListener('tickerSelectionChanged', handleTickerSelectionChanged);
    return () => {
      window.removeEventListener('tickerSelectionChanged', handleTickerSelectionChanged);
    };
  }, [fetchSelectedStatusTickers]);

  // Add a useEffect to fetch the last update time on component mount
  useEffect(() => {
    refreshLastUpdateTime();
  }, [refreshLastUpdateTime]);

  // Update time more frequently
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // Update every 10 seconds for more accurate display

    // Initial set
    setCurrentTime(new Date());

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!navOrderReady) {
      return;
    }
    if (navOrderPersistSkipRef.current) {
      navOrderPersistSkipRef.current = false;
      return;
    }
    persistNavOrder(navRow1, navRow2, navRow3);
  }, [navRow1, navRow2, navRow3, navOrderReady, persistNavOrder]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }
    const handleBadgeUpdate = (event) => {
      if (event?.detail && typeof event.detail.count === 'number') {
        setStopoutsCount(event.detail.count);
      }
    };
    const handleBasingBadge = (e) => { if (e?.detail && typeof e.detail.count === 'number') setBasingCount(e.detail.count); };
    const handleVcpBadge = (e) => { if (e?.detail && typeof e.detail.count === 'number') setVcpCount(e.detail.count); };
    const handleWatchlistBadge = (e) => { if (e?.detail && typeof e.detail.count === 'number') setWatchlistCount(e.detail.count); };
    const handleReadylistBadge = (e) => { if (e?.detail && typeof e.detail.count === 'number') setReadylistCount(e.detail.count); };
    const handleTickersBadge = (e) => { if (e?.detail && typeof e.detail.count === 'number') setTickersCount(e.detail.count); };
    const handleStorage = (event) => {
      if (event.key === 'stopouts_count') {
        const next = event.newValue ? parseInt(event.newValue, 10) || 0 : 0;
        setStopoutsCount(next);
      }
      if (event.key === 'basing_count') setBasingCount(event.newValue ? parseInt(event.newValue, 10) || 0 : 0);
      if (event.key === 'vcp_count') setVcpCount(event.newValue ? parseInt(event.newValue, 10) || 0 : 0);
      if (event.key === 'watchlist_count') setWatchlistCount(event.newValue ? parseInt(event.newValue, 10) || 0 : 0);
      if (event.key === 'readylist_count') setReadylistCount(event.newValue ? parseInt(event.newValue, 10) || 0 : 0);
      if (event.key === 'tickers_count') setTickersCount(event.newValue ? parseInt(event.newValue, 10) || 0 : 0);
    };
    window.addEventListener('stopouts:badge', handleBadgeUpdate);
    window.addEventListener('basing:badge', handleBasingBadge);
    window.addEventListener('vcp:badge', handleVcpBadge);
    window.addEventListener('watchlist:badge', handleWatchlistBadge);
    window.addEventListener('readylist:badge', handleReadylistBadge);
    window.addEventListener('tickers:badge', handleTickersBadge);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('stopouts:badge', handleBadgeUpdate);
      window.removeEventListener('basing:badge', handleBasingBadge);
      window.removeEventListener('vcp:badge', handleVcpBadge);
      window.removeEventListener('watchlist:badge', handleWatchlistBadge);
      window.removeEventListener('readylist:badge', handleReadylistBadge);
      window.removeEventListener('tickers:badge', handleTickersBadge);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const fetchPipelineCounts = useCallback(async () => {
    try {
      const { data } = await getPipelineCounts();
      const dispatch = (key, count) => {
        try { localStorage.setItem(key, String(count)); } catch (e) {}
      };
      setBasingCount(data.basing);
      dispatch('basing_count', data.basing);
      setVcpCount(data.vcp);
      dispatch('vcp_count', data.vcp);
      setWatchlistCount(data.watchlist);
      dispatch('watchlist_count', data.watchlist);
      setReadylistCount(data.readylist);
      dispatch('readylist_count', data.readylist);
    } catch (err) {
      // silent — badge counts are non-critical
    }
  }, []);

  useEffect(() => {
    fetchPipelineCounts();
    const handlePipelineChanged = () => fetchPipelineCounts();
    window.addEventListener('pipeline:changed', handlePipelineChanged);
    return () => window.removeEventListener('pipeline:changed', handlePipelineChanged);
  }, [fetchPipelineCounts]);

  // Format the current time in HH:MM format - use fresh time
  const getCurrentTimeString = () => {
    return new Date().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const NAV_ITEMS = useMemo(() => ({
    tickers: { label: 'Tickers', path: '/tickers' },
    transactions: { label: 'Trans', path: '/transactions' },
    trades: { label: 'Trades', path: '/trades', isActive: () => location.pathname === '/trades' || location.pathname.startsWith('/trades/') },
    orders: { label: 'Orders', path: '/orders' },
    plan: { label: 'Plan', path: '/plan' },
    livermore: { label: 'Livermore', path: '/livermore' },
    play: { label: 'Play', path: '/play' },
    wisdom: { label: 'Wisdom', path: '/wisdom' },
    strategies: { label: 'Strategies', path: '/strategies' },
    screen: { label: 'Screen', path: '/screen' },
    watchlist: { label: '2-Watch', path: '/watchlist' },
    ants: { label: 'Ants', path: '/ants' },
    newipos: { label: 'NewIPOs', path: '/new-ipos' },
    etfs: { label: 'ETFs', path: '/etfs' },
    stopouts: { label: 'Stopouts', path: '/stopouts' },
    steroids: { label: 'Steroids', path: '/steroids' },
    leaders: { label: 'Leaders', path: '/leaders' },
    vcp: { label: '1-VCP', path: '/vcp' },
    basing: { label: '0-Basing', path: '/basing' },
    eps: { label: 'GapUPs', path: '/eps' },
    athigh: { label: 'ATHigh', path: '/athigh' },
    turnarounds: { label: 'Turnarounds', path: '/turnarounds' },
    cyclicals: { label: 'Cyclicals', path: '/cyclicals' },
    gleaders: { label: 'GLeaders', path: '/gleaders', isActive: () => location.pathname.startsWith('/gleaders') },
    themes: { label: 'Themes', path: '/themes', isActive: () => location.pathname === '/themes' || location.pathname.startsWith('/themes/') },
    readylist: { label: '3-Ready', path: '/readylist' },
    inverses: { label: 'Inverses', path: '/inverses' },
    hvol: { label: 'HVol', path: '/hvol' },
    minervini: { label: 'Minervini', path: '/minervini' },
  }), [location.pathname]);

  const PIPELINE_COUNTS = { tickers: tickersCount, basing: basingCount, vcp: vcpCount, watchlist: watchlistCount, readylist: readylistCount };

  const renderNavLink = (key, row) => {
    const item = NAV_ITEMS[key];
    if (!item) return null;
    const active = item.isActive ? item.isActive() : location.pathname === item.path;
    const showStopoutsBadge = key === 'stopouts' && stopoutsCount > 0;
    const pipelineCount = PIPELINE_COUNTS[key];
    const showPipelineBadge = pipelineCount != null && pipelineCount > 0;
    const hasBadge = showStopoutsBadge || showPipelineBadge;
    const badgeStyle = {
      top: '-0.1rem',
      right: '-0.1rem',
      borderRadius: '50%',
      minWidth: '1.1rem',
      height: '1.1rem',
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.65rem'
    };
    return (
      <Nav.Link
        key={key}
        as={Link}
        to={item.path}
        active={active}
        className={hasBadge ? 'position-relative' : undefined}
        draggable
        onDragStart={(e) => handleNavDragStart(e, key, row)}
        onDragOver={handleNavDragOver}
        onDrop={(e) => handleNavDrop(e, key, row)}
        onDragEnd={handleNavDragEnd}
      >
        {item.label}
        {showStopoutsBadge && (
          <span className="badge bg-danger position-absolute" style={badgeStyle}>
            {stopoutsCount}
          </span>
        )}
        {showPipelineBadge && (
          <span className="badge position-absolute" style={{ ...badgeStyle, backgroundColor: '#ff6a00' }}>
            {pipelineCount}
          </span>
        )}
      </Nav.Link>
    );
  };

  const navRowBaseStyle = { marginBottom: 0, paddingBottom: 0, minHeight: '36px' };
  const navRowCompactStyle = { ...navRowBaseStyle, marginTop: '-10px' };

  const renderNavRowPlaceholder = (rowNumber) => (
    <div
      key={`nav-row-placeholder-${rowNumber}`}
      className="nav-row-placeholder flex-grow-1"
      onDragOver={handleNavDragOver}
      onDrop={(e) => handleNavRowDrop(e, rowNumber)}
    >
      <span className="small">Drag links here</span>
    </div>
  );

  // Format the last update time
  const getLastUpdateText = () => {
    if (!lastUpdateTime) return 'Last Update: Never';
    
    const now = new Date();
    const lastUpdate = new Date(lastUpdateTime);
    
    // Reject future dates, use current time instead
    if (isNaN(lastUpdate) || lastUpdate > now) {
      return 'Last Update: Just now (error)';
    }
    
    // Convert both dates to UTC for proper comparison
    // This removes the timezone discrepancy when calculating the time difference
    const diffMilliseconds = now.getTime() - lastUpdate.getTime();
    const diffMinutes = Math.floor(diffMilliseconds / (1000 * 60));
    
    // Regular relative format
    let relativeText = '';
    if (diffMinutes < 1) relativeText = 'Just now';
    else if (diffMinutes === 1) relativeText = '1 minute ago';
    else if (diffMinutes < 60) relativeText = `${diffMinutes} minutes ago`;
    else {
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours === 1) relativeText = '1 hour ago';
      else if (diffHours < 24) relativeText = `${diffHours} hours ago`;
      else relativeText = `${lastUpdate.toLocaleDateString()}`;
    }
    
    // Format time without seconds
    const formattedTime = lastUpdate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    // Check if we should show a "Market Closed" message
    // This happens when we have a marketInfo object with market_closed=true
    let marketClosedMessage = '';
    if (marketInfo && marketInfo.market_closed) {
      if (marketInfo.market_info?.status === 'weekend') {
        marketClosedMessage = ' [Weekend]';
      } else {
        marketClosedMessage = ' [Market Closed]';
      }
    }
    
    // Return the last update text with market closed message if applicable
    return `Last Update: ${relativeText} (${formattedTime})${marketClosedMessage}`;
  };

  // Function to get price update indicator text
  const getPriceUpdateIndicator = () => {
    if (!lastPriceUpdateTime) {
      return 'No price updates received';
    }
    
    const now = new Date();
    const lastPriceUpdate = new Date(lastPriceUpdateTime);
    const diffMinutes = Math.floor((now.getTime() - lastPriceUpdate.getTime()) / (1000 * 60));
    
    let timeText = '';
    if (diffMinutes < 1) timeText = 'just now';
    else if (diffMinutes === 1) timeText = '1 min ago';
    else if (diffMinutes < 60) timeText = `${diffMinutes} mins ago`;
    else {
      const diffHours = Math.floor(diffMinutes / 60);
      if (diffHours === 1) timeText = '1 hour ago';
      else timeText = `${diffHours} hours ago`;
    }
    
    return `Last price update: ${lastPriceUpdateCount} ticker${lastPriceUpdateCount !== 1 ? 's' : ''} ${timeText}`;
  };

  // Function to manually refresh price data
  const [isRefreshing] = useState(false);
  
  // Function to test WebSocket connectivity
  const [isTestingWebSocket, setIsTestingWebSocket] = useState(false);
  
  const handleTestWebSocket = async () => {
    if (isTestingWebSocket) return;
    
    try {
      setIsTestingWebSocket(true);
      logger.debug('=== WebSocket Test Started ===');
      
      // Call the debug endpoint to trigger a test WebSocket event
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/debug/test-websocket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      logger.debug('WebSocket test response', data);

      if (data.status === 'success') {
        logger.debug(`✅ WebSocket test sent to ${data.client_count} clients`);
      } else {
        logger.error('❌ WebSocket test failed:', data.message);
      }
    } catch (err) {
      logger.error('❌ Error testing WebSocket:', err);
    } finally {
      setIsTestingWebSocket(false);
    }
  };

  // Format percentage with + or - sign
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return '0.00%';
    }
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  // Get color class based on value
  const getColorClass = (value) => {
    if (value === null || value === undefined) return '';
    // Treat very small values (close to zero) as zero
    const absValue = Math.abs(value);
    if (absValue < 0.01) {
      return 'text-success'; // Show green for values close to zero
    }
    return value >= 0 ? 'text-success' : 'text-danger';
  };

  // Add useEffect to load username
  useEffect(() => {
    // Try to get username from localStorage
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) {
      setUsername(storedUsername);
    } else {
      // If username is not in localStorage, default to User
      setUsername('User');
    }
  }, [isAuthenticated]);

  // Add handleLogoutClick function to show confirmation modal
  const handleLogoutClick = () => {
    setShowProfileDropdown(false);
    setShowLogoutConfirmation(true);
  };

  // Execute the actual logout
  const handleConfirmLogout = (keepLoggedInForToday) => {
    setShowLogoutConfirmation(false);
    
    if (keepLoggedInForToday) {
      // Calculate midnight using a more reliable method to ensure timezone consistency
      const today = new Date();
      // Get the next day, set hours to 0, and ensure minutes, seconds, and milliseconds are 0
      const midnight = new Date(today);
      midnight.setDate(midnight.getDate() + 1);
      midnight.setHours(0, 0, 0, 0);
      
      // Store the expiration time in localStorage
      localStorage.setItem('keepLoggedInUntil', midnight.getTime().toString());

      // For debugging - log the expiration time
      logger.debug('Setting session to expire at', new Date(midnight).toLocaleString());
      
      // We don't call onLogout() here, since we want to keep the user logged in
    } else {
      // Regular logout
      onLogout();
    }
  };

  // Add useEffect to check for debug mode flag in localStorage
  useEffect(() => {
    const debugMode = localStorage.getItem('debugMode') === 'true';
    setShowDebugInfo(debugMode);
  }, []);

  // Handle outside clicks for index selector
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (indexSelectorRef.current && !indexSelectorRef.current.contains(event.target)) {
        setShowIndexSelector(false);
      }
    };

    if (showIndexSelector) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [showIndexSelector]);

  // Toggle debug mode function
  const toggleDebugMode = () => {
    const newDebugMode = !showDebugInfo;
    setShowDebugInfo(newDebugMode);
    localStorage.setItem('debugMode', String(newDebugMode));
  };

  // Market indexes management
  const [backendSelectedIndexes, setBackendSelectedIndexes] = useState(['SPY', 'DIA', 'QQQ']);
  // Kept in sync with backendSelectedIndexes so the mount-once WebSocket subscription
  // above can read the latest selection without needing to resubscribe on every change.
  const backendSelectedIndexesRef = useRef(backendSelectedIndexes);
  useEffect(() => {
    backendSelectedIndexesRef.current = backendSelectedIndexes;
  }, [backendSelectedIndexes]);

  const getSelectedIndexes = () => {
    return backendSelectedIndexes;
  };

  // Load selected indexes from backend on component mount
  useEffect(() => {
    if (isAuthenticated) {
      const loadSelectedIndexes = async () => {
        try {
          const response = await getSelectedMarketIndexes();
          if (response.data && response.data.success) {
            setBackendSelectedIndexes(response.data.selected_indexes);
          }
        } catch (error) {
          logger.error('Error loading selected market indexes:', error);
          // Keep default values on error
        }
      };
      loadSelectedIndexes();
    }
  }, [isAuthenticated]);

  const availableIndexes = [
    { symbol: 'SPY', name: 'S&P 500' },
    { symbol: 'DIA', name: 'Dow Jones' },
    { symbol: 'QQQ', name: 'NASDAQ-100' },
    { symbol: 'IWM', name: 'Russell 2000' },
    { symbol: 'RSP', name: 'S&P 500 Equal Weight' }
  ];

  const selectedSymbols = getSelectedIndexes();

  const handleIndexClick = (clickedSymbol, event) => {
    event.preventDefault();
    setShowIndexSelector(!showIndexSelector);
  };

  const MAX_SELECTED_SYMBOLS = 6;

  const handleTickerSelection = async (symbol, isChecked) => {
    let newSelection;
    
    if (isChecked && selectedSymbols.length < MAX_SELECTED_SYMBOLS && !selectedSymbols.includes(symbol)) {
      // Add new index
      newSelection = [...selectedSymbols, symbol].slice(-MAX_SELECTED_SYMBOLS);
    } else if (!isChecked && selectedSymbols.includes(symbol)) {
      // Remove index
      newSelection = selectedSymbols.filter(s => s !== symbol);
      if (newSelection.length === 0) {
        return; // Keep at least one
      }
    } else {
      return; // No change needed
    }
    
    // Save to backend
    try {
      const response = await saveSelectedMarketIndexes(newSelection);
      if (response.data && response.data.success) {
        setBackendSelectedIndexes(newSelection);
        setTimeRefresh(prev => prev + 1);
        logger.debug('Market index selection saved', newSelection);

        // Refresh ticker data to update selection
        try {
          const refreshResponse = await getSelectedStatusTickers();
          logger.debug('Refreshed status tickers after selection change', refreshResponse.data);
          setMarketIndexes(refreshResponse.data || []);
          // Dispatch custom event to notify chart component
          window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
            detail: { selection: newSelection }
          }));
        } catch (refreshError) {
          logger.error('Error refreshing status tickers:', refreshError);
        }
      }
    } catch (error) {
      logger.error('Error saving market index selection:', error);
      // Still update UI even if backend save fails
      setBackendSelectedIndexes(newSelection);
      setTimeRefresh(prev => prev + 1);
    }
  };

  // Handle adding custom ticker
  const handleAddCustomTicker = async () => {
    const symbol = customTickerInput.trim().toUpperCase();
    
    logger.debug('handleAddCustomTicker called with symbol', symbol);
    
    // Clear previous error
    setCustomTickerError('');
    
    // Basic validation
    if (!symbol) {
      setCustomTickerError('Please enter a ticker symbol');
      return;
    }
    
    if (symbol.length > 10) {
      setCustomTickerError('Ticker symbol too long (max 10 characters)');
      return;
    }
    
    if (!/^[A-Z0-9.-]+$/.test(symbol)) {
      setCustomTickerError('Invalid characters in ticker symbol');
      return;
    }
    
    if (selectedSymbols.includes(symbol)) {
      setCustomTickerError('Ticker already selected');
      return;
    }
    
    if (selectedSymbols.length >= MAX_SELECTED_SYMBOLS) {
      setCustomTickerError(`Maximum ${MAX_SELECTED_SYMBOLS} tickers allowed`);
      return;
    }
    
    try {
      // Validate ticker with backend API
      const response = await validateTicker(symbol);
      
      if (!response.data.valid) {
        setCustomTickerError(response.data.error || 'Ticker symbol not found');
        return;
      }
      
      // Add to selection
      const newSelection = [...selectedSymbols, symbol].slice(-MAX_SELECTED_SYMBOLS);
      
      // Save to backend
      const saveResponse = await saveSelectedMarketIndexes(newSelection);
      if (saveResponse.data && saveResponse.data.success) {
        setBackendSelectedIndexes(newSelection);
        setTimeRefresh(prev => prev + 1);
        setCustomTickerInput('');
        setCustomTickerError('');
        logger.debug('Custom ticker added', symbol);
        logger.debug('Save response', saveResponse);

        // Refresh ticker data to include the new custom ticker
        try {
          const response = await getSelectedStatusTickers();
          logger.debug('Refreshed status tickers after adding custom ticker', response.data);
          setMarketIndexes(response.data || []);
          // Dispatch custom event to notify chart component
          window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
            detail: { selection: newSelection }
          }));
        } catch (refreshError) {
          logger.error('Error refreshing status tickers:', refreshError);
        }
      }

    } catch (error) {
      logger.error('Error adding custom ticker:', error);
      if (error.response && error.response.data) {
        setCustomTickerError(error.response.data.error || 'Error validating ticker symbol');
      } else {
        setCustomTickerError('Network error - please try again');
      }
    }
  };

  // Handle removing custom ticker
  const handleRemoveCustomTicker = async (symbol) => {
    const newSelection = selectedSymbols.filter(s => s !== symbol);
    
    if (newSelection.length === 0) {
      return; // Keep at least one ticker
    }
    
    try {
      const response = await saveSelectedMarketIndexes(newSelection);
      if (response.data && response.data.success) {
        setBackendSelectedIndexes(newSelection);
        setTimeRefresh(prev => prev + 1);
        logger.debug('Custom ticker removed', symbol);

        // Refresh ticker data to remove the custom ticker
        try {
          const refreshResponse = await getSelectedStatusTickers();
          logger.debug('Refreshed status tickers after removing custom ticker', refreshResponse.data);
          setMarketIndexes(refreshResponse.data || []);
          // Dispatch custom event to notify chart component
          window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
            detail: { selection: newSelection }
          }));
        } catch (refreshError) {
          logger.error('Error refreshing status tickers:', refreshError);
        }
      }
    } catch (error) {
      logger.error('Error removing custom ticker:', error);
      // Still update UI even if backend save fails
      setBackendSelectedIndexes(newSelection);
      setTimeRefresh(prev => prev + 1);
    }
  };

  // Get seasonal button variant based on weather theme
  const getSeasonalButtonVariant = () => {
    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return isDark ? 'light' : 'primary';
    }
    
    switch (selectedWeatherTheme) {
      case 'spring':
        return 'success'; // Green
      case 'summer':
        return 'info'; // Blue
      case 'autumn':
      case 'fall':
        return 'warning'; // Orange
      case 'winter':
        return isDark ? 'light' : 'secondary'; // Gray/White
      default:
        return isDark ? 'light' : 'primary';
    }
  };

  // Get seasonal background color with transparency
  const getSeasonalBackground = () => {
    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
    
    const opacity = isDark ? 0.2 : 0.1; // Higher opacity for dark mode
    
    switch (selectedWeatherTheme) {
      case 'spring':
        return `rgba(40, 167, 69, ${opacity})`; // Green
      case 'summer':
        return `rgba(23, 162, 184, ${opacity})`; // Blue
      case 'autumn':
      case 'fall':
        return `rgba(255, 193, 7, ${opacity})`; // Orange
      case 'winter':
        return isDark ? 'rgba(248, 249, 250, 0.1)' : 'rgba(108, 117, 125, 0.1)'; // Gray
      default:
        return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
  };

  const handleDragStart = (e, symbol) => {
    e.dataTransfer.setData('text/plain', symbol);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItem(symbol);
  };

  const handleDragEnd = (e) => {
    setDraggedItem(null);
  };

  const moveNavItem = (dragKey, targetKey, targetRow) => {
    if (!dragKey || ![1, 2, 3].includes(targetRow)) {
      return;
    }

    const rows = {
      1: [...navRow1],
      2: [...navRow2],
      3: [...navRow3],
    };

    const removeKey = (arr) => {
      const idx = arr.indexOf(dragKey);
      if (idx !== -1) {
        arr.splice(idx, 1);
        return true;
      }
      return false;
    };

    const removed = removeKey(rows[1]) || removeKey(rows[2]) || removeKey(rows[3]);
    if (!removed) {
      return;
    }

    const insertInto = rows[targetRow];
    if (!insertInto) {
      return;
    }

    const targetIndex = targetKey ? insertInto.indexOf(targetKey) : -1;
    if (targetIndex === -1) {
      insertInto.push(dragKey);
    } else {
      insertInto.splice(targetIndex, 0, dragKey);
    }

    setNavRow1(rows[1]);
    setNavRow2(rows[2]);
    setNavRow3(rows[3]);
  };

  const handleNavDragStart = (e, key, row) => {
    setDraggingNavKey(key);
    setDraggingNavRow(row);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };

  const handleNavDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleNavDrop = (e, targetKey, targetRow) => {
    e.preventDefault();
    e.stopPropagation();
    const dragKey = draggingNavKey || e.dataTransfer.getData('text/plain');
    if (!dragKey || dragKey === targetKey) return;
    moveNavItem(dragKey, targetKey, targetRow);
    setDraggingNavKey(null);
    setDraggingNavRow(null);
  };

  const handleNavRowDrop = (e, targetRow) => {
    e.preventDefault();
    e.stopPropagation();
    const dragKey = draggingNavKey || e.dataTransfer.getData('text/plain');
    if (!dragKey) return;
    moveNavItem(dragKey, null, targetRow);
    setDraggingNavKey(null);
    setDraggingNavRow(null);
  };

  const handleNavDragEnd = () => {
    setDraggingNavKey(null);
    setDraggingNavRow(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetSymbol) => {
    e.preventDefault();
    const draggedSymbol = e.dataTransfer.getData('text/plain');
    
    if (draggedSymbol !== targetSymbol && selectedSymbols.includes(draggedSymbol) && selectedSymbols.includes(targetSymbol)) {
      // Reorder the selected indexes
      const newOrder = [...selectedSymbols];
      const draggedIndex = newOrder.indexOf(draggedSymbol);
      const targetIndex = newOrder.indexOf(targetSymbol);
      
      // Remove dragged item and insert at target position
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedSymbol);
      
      // Save to backend
      try {
        const response = await saveSelectedMarketIndexes(newOrder);
        if (response.data && response.data.success) {
          setBackendSelectedIndexes(newOrder);
          setTimeRefresh(prev => prev + 1);
          logger.debug('Market index order saved', newOrder);
          // Refresh ticker data and notify chart component
          try {
            const refreshResponse = await getSelectedStatusTickers();
            logger.debug('Refreshed status tickers after reordering', refreshResponse.data);
            setMarketIndexes(refreshResponse.data || []);
            // Dispatch custom event to notify chart component
            window.dispatchEvent(new CustomEvent('tickerSelectionChanged', {
              detail: { selection: newOrder }
            }));
          } catch (refreshError) {
            logger.error('Error refreshing status tickers:', refreshError);
          }
        }
      } catch (error) {
        logger.error('Error saving market index order:', error);
        // Still update UI even if backend save fails
        setBackendSelectedIndexes(newOrder);
        setTimeRefresh(prev => prev + 1);
      }
    }
    setDraggedItem(null);
  };

  return (
    <>
      <style>
        {`
          .navbar-dark .form-control::placeholder {
            color: rgba(255, 255, 255, 0.7);
          }
          .navbar-dark .form-control {
            color: white !important;
          }

          /* Enhanced active navigation link styling */
          .navbar-nav .nav-link.active {
            background-color: rgba(0, 123, 255, 0.2) !important;
            border-radius: 6px;
            font-weight: 600 !important;
            transition: all 0.2s ease-in-out;
          }

          .navbar-nav .nav-link:hover:not(.active) {
            background-color: rgba(0, 123, 255, 0.1);
            border-radius: 6px;
            transition: all 0.2s ease-in-out;
          }

          /* Dark mode active navigation styling */
          html[data-bs-theme="dark"] .navbar-nav .nav-link.active {
            background-color: rgba(255, 255, 255, 0.15) !important;
            color: #ffffff !important;
          }

          html[data-bs-theme="dark"] .navbar-nav .nav-link:hover:not(.active) {
            background-color: rgba(255, 255, 255, 0.08);
          }

          /* Enhanced dropdown menu active states */
          .list-group-item.active {
            background-color: rgba(0, 123, 255, 0.15) !important;
            border-color: rgba(0, 123, 255, 0.3) !important;
            color: #007bff !important;
            font-weight: 600 !important;
          }

          .list-group-item:hover:not(.active) {
            background-color: rgba(0, 123, 255, 0.05);
          }

          /* Dark mode dropdown styling */
          html[data-bs-theme="dark"] .list-group-item.active {
            background-color: rgba(255, 255, 255, 0.15) !important;
            border-color: rgba(255, 255, 255, 0.2) !important;
            color: #ffffff !important;
          }

          html[data-bs-theme="dark"] .list-group-item:hover:not(.active) {
            background-color: rgba(255, 255, 255, 0.05);
          }

          /* Dark mode styling for popovers */
          html[data-bs-theme="dark"] .popover .popover-header {
            background-color: #343a40;
            color: white;
            border-bottom: 1px solid #495057;
          }
          
          html[data-bs-theme="dark"] .popover .popover-body,
          html[data-bs-theme="dark"] .list-group-item {
            background-color: #212529;
            color: white;
          }
          
          html[data-bs-theme="dark"] .list-group-item.text-danger {
            color: #ea868f !important;
          }
          
          html[data-bs-theme="dark"] .list-group-item:hover {
            background-color: #2c3034;
          }

          /* Price update indicator styling */
          .status-container .text-success {
            color: #198754 !important;
          }
          
          .status-container .text-warning {
            color: #fd7e14 !important;
          }
          
          /* Dark mode support for status indicators */
          html[data-bs-theme="dark"] .status-container .text-success {
            color: #20c997 !important;
          }
          
          html[data-bs-theme="dark"] .status-container .text-warning {
            color: #ffc107 !important;
          }
          
          html[data-bs-theme="dark"] .status-container .text-muted {
            color: #adb5bd !important;
          }

          /* Connection indicator styling */
          .connection-indicator {
            font-size: 12px;
            margin-right: 4px;
          }
          
          .connection-indicator.connected {
            color: #198754;
          }
          
          .connection-indicator.connecting {
            color: #fd7e14;
            animation: pulse 1.5s infinite;
          }
          
          .connection-indicator.disconnected {
            color: #dc3545;
            animation: pulse 1s infinite;
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          /* Market indicator badge styling */
          .market-indicator-badge {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            transition: all 0.2s ease-in-out;
          }
          
          .market-indicator-badge:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          }
          
          /* Dark mode support for market indicator badge */
          html[data-bs-theme="dark"] .market-indicator-badge {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
          }
          
          html[data-bs-theme="dark"] .market-indicator-badge:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
          }

          /* Market condition options styling */
          .mc-option {
            transition: transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
            cursor: pointer;
            user-select: none;
          }
          .mc-option:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          }
          html[data-bs-theme="dark"] .mc-option:hover {
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
          }
          .mc-option.selected {
            outline: 3px solid #0d6efd33;
          }

          /* Market index selector dropdown styling */
          .market-index-dropdown {
            background-color: var(--bs-body-bg);
            border: 1px solid var(--bs-border-color);
            color: var(--bs-body-color);
          }
          
          .market-index-dropdown .btn-secondary {
            background-color: var(--bs-secondary);
            border-color: var(--bs-secondary);
            color: var(--bs-body-color);
          }
          
          /* Dark mode support for market index dropdown */
          html[data-bs-theme="dark"] .market-index-dropdown {
            background-color: #212529;
            border-color: #495057;
            color: #f8f9fa;
          }
          
          html[data-bs-theme="dark"] .market-index-dropdown .text-muted {
            color: #adb5bd !important;
          }
          
          html[data-bs-theme="dark"] .market-index-dropdown .border-top {
            border-color: #495057 !important;
          }
          
          html[data-bs-theme="dark"] .market-index-dropdown input[type="checkbox"] {
            filter: invert(1);
          }

          /* Drag and drop styling for market indexes */
          .draggable-index-item {
            cursor: grab;
            transition: all 0.2s ease;
          }
          
          .nav-row-placeholder {
            min-height: 32px;
            border: 1px dashed rgba(0, 123, 255, 0.4);
            border-radius: 4px;
            padding: 4px 12px;
            color: #6c757d;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 8px;
            font-style: italic;
            opacity: 0.8;
          }

          html[data-bs-theme="dark"] .nav-row-placeholder {
            border-color: rgba(255, 255, 255, 0.4);
            color: #adb5bd;
          }

          .draggable-index-item:hover {
            background-color: rgba(0, 123, 255, 0.1);
          }
          
          .draggable-index-item.dragging {
            opacity: 0.5;
            cursor: grabbing;
          }
          
          .draggable-index-item.drag-over {
            background-color: rgba(0, 123, 255, 0.2);
            border: 2px dashed #007bff;
          }
          
          .drag-handle {
            color: #6c757d;
            margin-right: 8px;
            cursor: grab;
          }
          
          html[data-bs-theme="dark"] .draggable-index-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }
          
          html[data-bs-theme="dark"] .draggable-index-item {
            border-color: #495057 !important;
          }
          
          html[data-bs-theme="dark"] .drag-handle {
            color: #adb5bd;
          }

          /* Collapsible navbar height adjustment */
          .title-navbar {
            transition: padding 0.3s ease-in-out;
          }

          .title-navbar.collapsed {
            padding-top: 0.25rem;
            padding-bottom: 0.25rem;
          }

          .title-navbar.collapsed .navbar-collapse {
            padding-top: 0;
            padding-bottom: 0;
          }

          .title-navbar.collapsed .container {
            padding-top: 0;
            padding-bottom: 0;
          }

          /* Smooth transition for nav rows */
          .nav-rows-container {
            transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
            overflow: hidden;
          }

          .nav-rows-container.collapsed {
            max-height: 0;
            opacity: 0;
          }

          .nav-rows-container.expanded {
            max-height: 200px;
            opacity: 1;
          }
        `}
      </style>

      <Navbar
        bg={navbarBg}
        variant={navbarVariant}
        expand="lg"
        className={`title-navbar ${isDark ? 'title-navbar-dark' : 'title-navbar-light'} ${isNavCollapsed ? 'collapsed' : ''}`}
        style={{
          paddingTop: isNavCollapsed ? '0.25rem' : undefined,
          paddingBottom: isNavCollapsed ? '0.25rem' : undefined
        }}
      >
        <Container
          className="align-items-start"
          style={{
            alignItems: 'flex-start',
            paddingTop: isNavCollapsed ? '0.25rem' : undefined,
            paddingBottom: isNavCollapsed ? '0.25rem' : undefined
          }}
        >
          {isAuthenticated && (
            <Button
              variant="outline-light"
              className="me-2 d-lg-none mobile-nav-toggle"
              onClick={toggleMobileNav}
              aria-label={isMobileNavVisible ? "Close navigation" : "Open navigation"}
            >
              <i className={`bi ${isMobileNavVisible ? 'bi-x-lg' : 'bi-list'}`}></i>
            </Button>
          )}
          <div className="d-flex align-items-center">
            <Navbar.Brand as={Link} to="/all-accounts" className="mb-0 pb-0" style={{ color: '#007bff' }}>
              Investing Tracker
            </Navbar.Brand>
            {isAuthenticated && (
              <Button
                variant="link"
                className="p-0 ms-2 text-decoration-none"
                onClick={() => setIsNavCollapsed(!isNavCollapsed)}
                style={{
                  fontSize: '1.2rem',
                  color: isDark ? '#adb5bd' : '#6c757d',
                  lineHeight: 1
                }}
                title={isNavCollapsed ? "Expand navigation" : "Collapse navigation"}
              >
                <i className={`bi ${isNavCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`}></i>
              </Button>
            )}
          </div>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav" className="align-items-start">
            <div className="d-flex flex-column w-100" style={{ gap: 0 }}>
              <Nav
                className="me-auto flex-wrap"
                style={navRowBaseStyle}
                onDragOver={handleNavDragOver}
                onDrop={(e) => handleNavRowDrop(e, 1)}
              >
                {isAuthenticated && navRow1.map(key => renderNavLink(key, 1))}
              </Nav>
              <div className={`nav-rows-container ${isNavCollapsed ? 'collapsed' : 'expanded'}`}>
                <Nav
                  className="me-auto flex-wrap"
                  style={navRowCompactStyle}
                  onDragOver={handleNavDragOver}
                  onDrop={(e) => handleNavRowDrop(e, 2)}
                >
                  {isAuthenticated && navRow2.map(key => renderNavLink(key, 2))}
                </Nav>
                <Nav
                  className="me-auto flex-wrap"
                  style={navRowCompactStyle}
                  onDragOver={handleNavDragOver}
                  onDrop={(e) => handleNavRowDrop(e, 3)}
                >
                  {isAuthenticated && navRow3.map(key => renderNavLink(key, 3))}
                  {isAuthenticated && navRow3.length === 0 && renderNavRowPlaceholder(3)}
                </Nav>
              </div>
            </div>
            
            <Nav className="align-items-start">
              {isAuthenticated ? (
                <>
                  <div className="ms-2 position-relative">
                    <Button
                      ref={profileRef}
                      variant={isDark ? 'outline-light' : 'outline-dark'}
                      onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                      className="d-flex align-items-start"
                    >
                      <i className="bi bi-person-circle me-1"></i>
                      <span className="d-none d-md-inline">{username}</span>
                    </Button>
                    <Overlay
                      show={showProfileDropdown}
                      target={profileRef.current}
                      placement="bottom-end"
                      container={profileRef}
                      containerPadding={20}
                      rootClose
                      onHide={() => setShowProfileDropdown(false)}
                    >
                      <Popover id="profile-popover" style={{ minWidth: '200px' }}>
                        <Popover.Body className="p-0">
                          <div className="list-group list-group-flush">
                            <Link
                              to="/profile"
                              className={`list-group-item list-group-item-action ${location.pathname === '/profile' ? 'active' : ''}`}
                              onClick={() => setShowProfileDropdown(false)}
                            >
                              <i className="bi bi-person me-2"></i>
                              My Profile
                            </Link>
                            <Link
                              to="/settings"
                              className={`list-group-item list-group-item-action ${location.pathname === '/settings' ? 'active' : ''}`}
                              onClick={() => setShowProfileDropdown(false)}
                            >
                              <i className="bi bi-gear me-2"></i>
                              Settings
                            </Link>
                            {localStorage.getItem('isAdmin') === 'true' && (
                              <>
                                <Link
                                  to="/admin-settings"
                                  className={`list-group-item list-group-item-action ${location.pathname === '/admin-settings' ? 'active' : ''}`}
                                  onClick={() => setShowProfileDropdown(false)}
                                >
                                  <i className="bi bi-gear-fill me-2"></i>
                                  Admin Settings
                                </Link>
                                <Link
                                  to="/activity-log"
                                  className={`list-group-item list-group-item-action ${location.pathname === '/activity-log' ? 'active' : ''}`}
                                  onClick={() => setShowProfileDropdown(false)}
                                >
                                  <i className="bi bi-clock-history me-2"></i>
                                  Activity Log
                                </Link>
                              </>
                            )}
                            <div
                              className="list-group-item list-group-item-action text-danger"
                              onClick={handleLogoutClick}
                              style={{ cursor: 'pointer' }}
                            >
                              <i className="bi bi-box-arrow-right me-2"></i>
                              Logout
                            </div>
                          </div>
                        </Popover.Body>
                      </Popover>
                    </Overlay>
                  </div>
                </>
              ) : (
                <>
                  <Nav.Link 
                    as={Link} 
                    to="/login" 
                    active={location.pathname === '/login'}
                  >
                    Login
                  </Nav.Link>
                  {shouldShowRegisterLink && (
                    <Nav.Link 
                      as={Link} 
                      to="/register" 
                      active={location.pathname === '/register'}
                    >
                      Register
                    </Nav.Link>
                  )}
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      
      {/* Status bar with date, market indexes, and last update information */}
      {isAuthenticated && (
        <div className={`py-1 d-none d-lg-block title-status-bar ${isDark ? 'title-status-bar-dark' : 'title-status-bar-light'}`}>
          <Container className="d-flex justify-content-between align-items-center">
            <div className={`d-flex align-items-center ${isDark ? 'text-light' : 'title-status-text'}`}>
              {/* Clock and Calculator */}
              <div className="d-flex align-items-center me-3">
                <div
                  ref={clockRef}
                  className="d-flex align-items-center text-muted small me-2"
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setIsClockDetached(!isClockDetached)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setIsClockDetached(!isClockDetached);
                    }
                  }}
                  title="Click to detach clock"
                >
                  <i className="bi bi-clock me-1"></i>
                  <span>{getCurrentTimeString()}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-link p-0 text-muted"
                  style={{ fontSize: '1rem', textDecoration: 'none' }}
                  onClick={() => setIsCalculatorDetached(!isCalculatorDetached)}
                  title="Click to open calculator"
                >
                  <i className="bi bi-calculator"></i>
                </button>
              </div>

              {/* App Version */}
              <AppVersion />

              {/* Market Indicator Badge */}
              <span className="ms-3 d-flex align-items-center">
                <span className="me-2 text-muted small">Market:</span>
                <div
                  className="market-indicator-badge"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    border: '2px solid',
                    backgroundColor: marketConditionOption.bg,
                    borderColor: marketConditionOption.border,
                    color: marketConditionOption.text,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  title={`Market Condition: ${marketConditionOption.title} (${marketConditionOption.percent})`}
                  onClick={() => setShowMarketConditionModal(true)}
                >
                  <span className="me-1">
                    <span style={{ color: marketConditionOption.border }}>{marketConditionOption.icon}</span>
                  </span>
                  {marketConditionOption.percent}
                </div>
                <span
                  className="ms-2 small"
                  style={{
                    color: isDark ? marketConditionDarkTextColor : marketConditionOption.text
                  }}
                >
                  {marketConditionOption.note}
                </span>
              </span>

              {isClockDetached && (
                <DetachableClock 
                  onClose={() => setIsClockDetached(false)} 
                />
              )}

              {isCalculatorDetached && (
                <DetachableCalculator 
                  onClose={() => setIsCalculatorDetached(false)} 
                />
              )}

              <StrategyPresetModal
                show={isStrategyModalVisible}
                onClose={handleStrategyModalClose}
              />
            </div>

            {/* Market Indexes */}
            <div className="d-flex justify-content-center align-items-center">
              <div className="position-relative" ref={indexSelectorRef}>
                <div className="d-flex justify-content-center align-items-center">
                  {selectedSymbols.map((symbol, idx) => {
                    // Find matching index from backend data
                    const index = marketIndexes.find(i => i && i.symbol === symbol) || { symbol };
                    const hasData = index.current_price !== null && index.current_price !== undefined;
                    const indexInfo = availableIndexes.find(ai => ai.symbol === symbol);
                    
                    return (
                      <div key={symbol} className="d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
                        <span
                          className="fw-bold me-1"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => handleIndexClick(symbol, e)}
                          title={`Click to change ${indexInfo?.name || symbol}`}
                        >
                          {symbol}
                        </span>
                        <span className={hasData ? getColorClass(index.percent_change) : 'text-muted'}>
                          {hasData ? formatPercentage(index.percent_change) : 'N/A'}
                        </span>
                        {idx < selectedSymbols.length - 1 &&
                          <span className="mx-1 text-muted">|</span>}
                      </div>
                    );
                  })}
                </div>
                
                {showIndexSelector && (
                  <div 
                    className="position-absolute rounded shadow-lg p-3 mt-1"
                    style={{ 
                      top: '100%', 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      zIndex: 1000,
                      minWidth: '320px',
                      width: 'max-content',
                      backgroundColor: isDark ? '#212529' : '#ffffff',
                      border: `1px solid ${isDark ? '#495057' : '#dee2e6'}`,
                      color: isDark ? '#f8f9fa' : '#212529'
                    }}
                  >
                    <div className="small fw-bold mb-2 text-center">Select Ticker Symbols (max {MAX_SELECTED_SYMBOLS}):</div>
                    
                    {/* Add Custom Ticker Section */}
                    <div className="mb-3 p-2 rounded" style={{ 
                      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                      border: `1px solid ${isDark ? '#495057' : '#dee2e6'}`
                    }}>
                      <div className="small fw-bold mb-2" style={{ color: isDark ? '#adb5bd' : '#6c757d' }}>
                        <i className="bi bi-plus-circle me-1"></i>
                        Add Custom Ticker:
                      </div>
                      <div className="d-flex" style={{ gap: '0.5rem' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Enter ticker symbol (e.g., AAPL, TSLA)"
                          value={customTickerInput}
                          onChange={(e) => setCustomTickerInput(e.target.value.toUpperCase())}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddCustomTicker();
                            }
                          }}
                          maxLength={10}
                          style={{
                            backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#ffffff',
                            color: isDark ? '#ffffff' : '#212529',
                            border: `1px solid ${isDark ? '#495057' : '#ced4da'}`
                          }}
                        />
                        <button
                          type="button"
                          className={`btn btn-sm btn-${getSeasonalButtonVariant()}`}
                          onClick={handleAddCustomTicker}
                          disabled={!customTickerInput.trim() || selectedSymbols.length >= MAX_SELECTED_SYMBOLS}
                          title={selectedSymbols.length >= MAX_SELECTED_SYMBOLS ? `Maximum ${MAX_SELECTED_SYMBOLS} tickers allowed` : "Add ticker"}
                        >
                          <i className="bi bi-plus"></i>
                        </button>
                      </div>
                      {customTickerError && (
                        <div className="small text-danger mt-1">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          {customTickerError}
                        </div>
                      )}
                      {selectedSymbols.length >= MAX_SELECTED_SYMBOLS && !customTickerError && (
                        <div className="small text-warning mt-1">
                          <i className="bi bi-info-circle me-1"></i>
                          Remove a ticker first to add a new one (max {MAX_SELECTED_SYMBOLS} allowed)
                        </div>
                      )}
                    </div>
                    
                    <div className="mb-2">
                      <div className="small mb-2" style={{ color: isDark ? '#adb5bd' : '#6c757d' }}>
                        <i className="bi bi-arrows-move me-1"></i>
                        Drag to reorder (left ← → right in status bar):
                      </div>
                      
                      {/* Show selected tickers in order for reordering */}
                      {selectedSymbols.map((symbol, index) => {
                        const indexInfo = availableIndexes.find(ai => ai.symbol === symbol);
                        const isCustomTicker = !indexInfo;
                        return (
                          <div
                            key={symbol}
                            draggable
                            onDragStart={(e) => handleDragStart(e, symbol)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, symbol)}
                            className={`draggable-index-item d-flex justify-content-between align-items-center py-2 px-2 rounded mb-1 ${
                              draggedItem === symbol ? 'dragging' : ''
                            }`}
                            style={{ 
                              border: `1px solid ${isDark ? '#495057' : '#dee2e6'}`,
                              backgroundColor: getSeasonalBackground()
                            }}
                          >
                            <div className="d-flex align-items-center flex-grow-1">
                              <i 
                                className="bi bi-grip-vertical" 
                                style={{ 
                                  color: isDark ? '#adb5bd' : '#6c757d',
                                  marginRight: '8px',
                                  cursor: 'grab'
                                }}
                              ></i>
                              <span className="small fw-bold me-1">#{index + 1}</span>
                              <span className="small">
                                <strong>{symbol}</strong> 
                                {isCustomTicker ? (
                                  <span className="text-muted ms-1">(Custom)</span>
                                ) : (
                                  <span> - {indexInfo.name}</span>
                                )}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              {isCustomTicker && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleRemoveCustomTicker(symbol)}
                                  title="Remove custom ticker"
                                  style={{ 
                                    padding: '0.125rem 0.375rem',
                                    fontSize: '0.7rem',
                                    lineHeight: '1'
                                  }}
                                >
                                  <i className="bi bi-trash3"></i>
                                </button>
                              )}
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={(e) => handleTickerSelection(symbol, e.target.checked)}
                                style={{ filter: isDark ? 'invert(1)' : 'none' }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div 
                      className="small fw-bold mb-2 pt-2" 
                      style={{ 
                        borderTop: `1px solid ${isDark ? '#495057' : '#dee2e6'}` 
                      }}
                    >
                      Available Indexes:
                    </div>
                    {availableIndexes
                      .filter(availIndex => !selectedSymbols.includes(availIndex.symbol))
                      .map(availIndex => (
                        <div 
                          key={availIndex.symbol}
                          className="d-flex justify-content-between align-items-center py-1 px-2 rounded"
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="small">
                            <strong>{availIndex.symbol}</strong> - {availIndex.name}
                          </span>
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={(e) => handleTickerSelection(availIndex.symbol, e.target.checked)}
                            style={{ filter: isDark ? 'invert(1)' : 'none' }}
                          />
                        </div>
                      ))}
                    <div className="text-center mt-2">
                      <button 
                        className={`btn btn-sm btn-outline-${getSeasonalButtonVariant()}`}
                        onClick={() => setShowIndexSelector(false)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Search ticker and status section */}
            <div className="d-flex align-items-center">
              {/* Search ticker form */}
              {isAuthenticated && (
                <div className="me-3" style={{ position: 'relative', width: '150px' }}>
                  <i className="bi bi-search" style={{
                    position: 'absolute', left: '0.4rem', top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px',
                    color: isDark ? 'rgba(255,255,255,0.5)' : '#6c757d',
                    pointerEvents: 'none', zIndex: 2
                  }}></i>
                  <Form.Control
                    type="text"
                    placeholder="Search ticker"
                    value={searchTicker}
                    onChange={(e) => setSearchTicker(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchNavigate(searchTicker); }}
                    size="sm"
                    className={isDark ? 'border-0' : ''}
                    style={{
                      paddingLeft: '1.6rem',
                      paddingRight: '1.6rem',
                      backgroundColor: isDark ? 'rgba(255,255,255,0.15)' : 'var(--input-bg)',
                      color: isDark ? '#ffffff' : '#212529',
                      border: isDark ? 'none' : '1px solid var(--border-color)',
                      borderRadius: '4px',
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSearchClear}
                    disabled={!searchTicker}
                    title="Clear search"
                    style={{
                      position: 'absolute', right: '0.3rem', top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none', background: 'transparent',
                      color: isDark ? 'rgba(255,255,255,0.6)' : '#6c757d',
                      padding: 0, lineHeight: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      opacity: searchTicker ? 1 : 0.25,
                      cursor: searchTicker ? 'pointer' : 'default',
                      transition: 'opacity 0.15s',
                      zIndex: 2,
                    }}
                  >
                    <i className="bi bi-x-circle" style={{ fontSize: '13px' }}></i>
                  </button>
                </div>
              )}

              {/* Compact status button */}
              <div className="status-container d-flex align-items-center">
                <HeartbeatIndicator
                  lastUpdateTime={lastPriceUpdateTime}
                  updateIntervalMinutes={updateIntervalMinutes}
                  isActive={marketInfo ? !marketInfo.market_closed : true}
                  socketStatus={socketStatus}
                  distributedStatus={distributedStatus}
                  lockStatus={lockStatus}
                  rateLimitStatus={rateLimitStatus}
                  size={24}
                  isWebSocketConnected={isWebSocketConnected}
                  isPollingActive={isPollingActive}
                  lastUpdateSource={lastUpdateSource}
                  totalUpdates={totalUpdates}
                  webSocketUpdates={webSocketUpdates}
                  pollingUpdates={pollingUpdates}
                  pollingStatus={pollingStatus}
                  setPollingInterval={setPollingInterval}
                />
                <div className="ms-2">
                  <StatusButton
                    socketStatus={socketStatus}
                    lastUpdateTime={lastUpdateTime}
                    lastPriceUpdateTime={lastPriceUpdateTime}
                    lastPriceUpdateCount={lastPriceUpdateCount}
                    isRefreshing={isRefreshing}
                    showDebugInfo={showDebugInfo}
                    getLastUpdateText={getLastUpdateText}
                    getPriceUpdateIndicator={getPriceUpdateIndicator}
                    handleTestWebSocket={handleTestWebSocket}
                    isTestingWebSocket={isTestingWebSocket}
                    toggleDebugMode={toggleDebugMode}
                    updateIntervalMinutes={updateIntervalMinutes}
                  />
                </div>
              </div>
            </div>
          </Container>
        </div>
      )}

      {/* Add Logout Confirmation Modal */}
      <LogoutConfirmation 
        show={showLogoutConfirmation}
        onHide={() => setShowLogoutConfirmation(false)}
        onLogout={handleConfirmLogout}
      />

      <Modal show={showMarketConditionModal} onHide={() => setShowMarketConditionModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Pick Market Condition</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-3">Click a condition to apply immediately.</div>
          {(() => {
            const options = MARKET_CONDITION_OPTIONS;

            const handlePick = async (val) => {
              try {
                await updateMarketCondition(val);
                setShowMarketConditionModal(false);
              } catch {}
            };

            return (
              <div className="d-flex flex-column gap-3">
                {options.map(opt => (
                  <div
                    key={opt.value}
                    className={`mc-option p-3 rounded-3 border ${marketCondition === opt.value ? 'selected' : ''}`}
                    style={{
                      backgroundColor: opt.bg,
                      borderColor: opt.border,
                      color: opt.text
                    }}
                    onClick={() => handlePick(opt.value)}
                    role="button"
                    aria-label={`${opt.title} (${opt.percent})`}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <span className="me-2" style={{ fontSize: '1.4rem' }}>{opt.icon}</span>
                      <div className="fw-bold" style={{ fontSize: '1.05rem' }}>{opt.percent} – {opt.title}</div>
                    </div>
                    {opt.note && <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-bold" style={{ color: opt.text, fontSize: '1.05rem' }}>{opt.note}</div>
                      <div className="small" style={{ color: opt.text }}>{opt.desc}</div>
                    </div>}
                  </div>
                ))}
              </div>
            );
          })()}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Navigation; 
